import * as React from 'react'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { TableHead } from '@mui/material'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import ResetTemporaryPassword from '../../../../components/dialogs/ResetTemporaryPassword'
import ArrowRightIcon from '../../../../assets/icons/Arrows-chevron/Arrow-Right.svg'
import { capitalize } from '../../../../utils/functions'
import { CuratorStudentState } from '../../../../features/types'
import ProgressBar from '../../../../components/progressBar/ProgressBar'
import { tableStyles, ui, tone } from '../../../../utils/uiTokens'
import styled from '@emotion/styled'
import HistoryIcon from '@mui/icons-material/History';
import { useHeadScope } from '../../../head/headScope'
import { IDLE_ALERT_DAYS, LastSeen, studentActivityApi } from '../../../../features/curator/studentActivity'

// Соңғы кіру подгружается пачкой на каждую новую порцию строк, а не по запросу на ученика
const LAST_SEEN_CHUNK = 100

interface Props {
    studentsData: CuratorStudentState[]
    isFetching: boolean
    onResetPassword?: (id: number) => void
    sortColumn?: number
    sortDir?: number
    onSort?: (column: number) => void
    courseSubjectId?: number
}

// Заголовок-кнопка со стрелками ▲▼: активное направление подсвечено
const SortableHeader: React.FC<{
    label: string
    column: number
    sortColumn?: number
    sortDir?: number
    onSort?: (column: number) => void
}> = ({ label, column, sortColumn, sortDir, onSort }) => {
    const active = sortColumn === column
    return (
        <HeaderButton type="button" onClick={() => onSort?.(column)}>
            {label}
            <Arrows>
                <span style={{ color: active && sortDir === 2 ? ui.pink : ui.muted }}>▲</span>
                <span style={{ color: active && sortDir === 1 ? ui.pink : ui.muted }}>▼</span>
            </Arrows>
        </HeaderButton>
    )
}

const StudentsTable: React.FC<Props> = ({
    studentsData = [],
    isFetching,
    onResetPassword,
    sortColumn,
    sortDir,
    onSort,
    courseSubjectId,
}) => {
    const { formatMessage, locale } = useIntl()
    // пән басшы не видит телефонов и паролей и не сбрасывает их
    const { readOnly, basePath } = useHeadScope()
    // карточка открывается на предмете выбранной группы, а не на первом
    const linkOf = (row: CuratorStudentState) =>
        `${basePath}/students/${row?.userCourseEnrollId}?fullName=${row?.firstName}-${row?.lastName}` +
        (courseSubjectId ? `&courseSubjectId=${courseSubjectId}` : '')
    const [fetchLastSeen] = studentActivityApi.useLazyFetchLastSeenQuery()
    const [lastSeen, setLastSeen] = React.useState<Record<number, LastSeen | null>>({})
    const requestedRef = React.useRef<Set<number>>(new Set())

    React.useEffect(() => {
        const missing = studentsData.map((row) => row.userId).filter((id) => id && !requestedRef.current.has(id))
        for (let i = 0; i < missing.length; i += LAST_SEEN_CHUNK) {
            const chunk = missing.slice(i, i + LAST_SEEN_CHUNK)
            chunk.forEach((id) => requestedRef.current.add(id))
            fetchLastSeen(chunk, true).unwrap().then((list) => {
                const next: Record<number, LastSeen | null> = {}
                chunk.forEach((id) => { next[id] = null })
                list.forEach((x) => { next[x.userId] = x })
                setLastSeen((prev) => ({ ...prev, ...next }))
            }).catch(() => {
                chunk.forEach((id) => requestedRef.current.delete(id))
            })
        }
    }, [studentsData])

    const lastSeenCell = (userId: number) => {
        if (!(userId in lastSeen)) return null
        const x = lastSeen[userId]
        const alert = !x?.lastSeenAt || (x.idleDays ?? 0) >= IDLE_ALERT_DAYS
        const text = !x?.lastSeenAt
            ? formatMessage({ id: 'sxNever' })
            : x.idleDays === 0
                ? formatMessage({ id: 'sxToday' })
                : x.idleDays === 1
                    ? formatMessage({ id: 'sxYesterday' })
                    : formatMessage({ id: 'sxDaysAgo' }, { n: x.idleDays })
        return (
            <Box sx={{ color: alert ? tone.bad.text : ui.ink, fontWeight: alert ? 600 : 400, whiteSpace: 'nowrap' }}>
                {text}
                {x?.lastSeenAt && (
                    <Box sx={{ color: ui.muted, fontSize: '12px', fontWeight: 400 }}>
                        {x.lastSeenAt.slice(0, 10).split('-').reverse().join('.')}
                    </Box>
                )}
            </Box>
        )
    }

    // карточка ученика листает соседей в том же порядке, что и таблица
    const siblings = studentsData.map((row) => ({ id: row.userCourseEnrollId, to: linkOf(row), student: row }))

    const getDuration = (daysLeft?: number) => {
        if (daysLeft > 30) {
            return (
                <>
                    <Medium>
                        {Math.floor(daysLeft / 30) + ' ' + formatMessage({ id: 'month' })}
                    </Medium>
                    <DayLabel>
                        <HistoryIcon sx={{ fontSize: 18 }} />
                        {daysLeft % 30 + ' ' + formatMessage({ id: 'day' })}
                    </DayLabel>
                </>)
        } else if (daysLeft === 0) {
            return <FinishedLabel>✓ {formatMessage({ id: 'courseCompleted' })}</FinishedLabel>
        } else {
            return (
                <DayLabel>
                    <HistoryIcon sx={{ fontSize: 18 }} />
                    {daysLeft + ' ' + formatMessage({ id: 'day' })}
                </DayLabel>
            )
        }
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={tableStyles.container}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={tableStyles.head}>
                        <TableRow>
                            <TableCell>#</TableCell>

                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            {!readOnly && (
                                <TableCell>
                                    {formatMessage({ id: 'phone' })}
                                </TableCell>
                            )}

                            {!readOnly && (
                                <TableCell>
                                    {formatMessage({ id: 'temporaryPassword' })}
                                </TableCell>
                            )}

                            <TableCell align="center">
                                {formatMessage({ id: 'performance' })}
                            </TableCell>

                            <TableCell>
                                <SortableHeader
                                    label={capitalize(formatMessage({ id: 'videoLesson' }))}
                                    column={1}
                                    sortColumn={sortColumn}
                                    sortDir={sortDir}
                                    onSort={onSort}
                                />
                            </TableCell>

                            <TableCell>
                                <SortableHeader
                                    label={formatMessage({ id: 'test' })}
                                    column={2}
                                    sortColumn={sortColumn}
                                    sortDir={sortDir}
                                    onSort={onSort}
                                />
                            </TableCell>

                            <TableCell>
                                <SortableHeader
                                    label={formatMessage({ id: 'sxLastSeen' })}
                                    column={3}
                                    sortColumn={sortColumn}
                                    sortDir={sortDir}
                                    onSort={onSort}
                                />
                            </TableCell>

                            <TableCell align="center">
                                {formatMessage({ id: 'courseDuration' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'details' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>
                    {studentsData.length ? (
                        <TableBody>
                            {studentsData.map((row, index) => (
                                <TableRow key={row.userId} sx={tableStyles.row}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {index + 1}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row.firstName} {row.lastName}
                                    </TableCell>

                                    {!readOnly && (
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row.mobile}
                                    </TableCell>
                                    )}

                                    {!readOnly && (
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        <ResetTemporaryPassword
                                            password={row.password}
                                            fullName={
                                                row.lastName +
                                                ' ' +
                                                row.firstName
                                            }
                                            isLoading={isFetching}
                                            confirmAction={() =>
                                                onResetPassword(row.userId)
                                            }
                                        />
                                    </TableCell>
                                    )}

                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        {row.performanceLevel && <StatusSpan performanceLevel={row.performanceLevel}>{formatMessage({ id: `${row.performanceLevel}` })}</StatusSpan>}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        <ProgressBar value={row.watchedVideoPercentage} />
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        <ProgressBar value={row.topicTestPercentage} />
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {lastSeenCell(row.userId)}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                    >
                                        <LabelBox>
                                            {getDuration(row?.daysLeft)}
                                        </LabelBox>
                                    </TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="right"
                                    >
                                        <Link to={linkOf(row)} state={{ student: row, siblings }}>
                                            <IconButton
                                                sx={{ background: ui.pinkSoft, borderRadius: '10px' }}
                                            >
                                                <img
                                                    src={ArrowRightIcon}
                                                    alt="edit"
                                                />
                                            </IconButton>
                                        </Link>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    ) : (
                        !isFetching && (
                            <TableBody>
                                <TableRow>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={10}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        )
                    )}

                </Table>
            </TableContainer>
        </Box>
    )
}

export default StudentsTable


const HeaderButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    font: inherit;
    color: inherit;
    user-select: none;
`

const Arrows = styled.span`
    display: inline-flex;
    flex-direction: column;
    line-height: 0.75;
    font-size: 9px;
`

const FinishedLabel = styled.span`
    color: ${tone.ok.text};
    font-weight: 500;
    font-size: 16px;
    background-color: ${tone.ok.border};
    border: 4px solid ${tone.ok.border};
    border-radius: 100px;
    padding: 2px 12px;
`

const Medium = styled.span`
    font-size: 16px;
    font-weight: 600;
    color: ${ui.ink};
`

const LabelBox = styled.div`
    display: flex;
    flex-direction: column;
    text-align: center;
    align-items: center;
`

const DayLabel = styled.span`
    font-size: 14px;
    color: ${ui.sub};
    display: flex;
    align-items: center;
    gap: 2px;
`

const StatusSpan = styled.span<{ performanceLevel: string }>`
    background-color: ${({ performanceLevel }) => {
        switch (performanceLevel) {
            case 'ACTIVE':
                return tone.ok.bg;
            case 'MEDIUM':
                return tone.info.bg;
            case 'LOW':
                return tone.warn.bg;
            case 'NEEDS_HELP':
                return tone.bad.bg;
            default:
                return ui.track;
        }
    }};
    color: ${({ performanceLevel }) => {
        switch (performanceLevel) {
            case 'ACTIVE':
                return tone.ok.text;
            case 'MEDIUM':
                return tone.info.text;
            case 'LOW':
                return tone.warn.text;
            case 'NEEDS_HELP':
                return tone.bad.text;
            default:
                return ui.ink;
        }
    }};
    border-radius: 100px;
    padding: 6px 12px;
    font-weight: 600;
    font-size: 14px;
`